import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useParams } from "react-router-dom";
import { useStoresSummaryStoresOnly } from "features/Stores";
import type { MovedToBackstockItem } from "models/audit/AuditDetailModel";
import {
  useAuditDashboardDailyExpandedReport,
  useAuditDashboardManualSoldOutEligibility,
  useMarkMovedPackSoldOutMutation,
} from "../../hooks";
import { auditDashboardTheme } from "../../theme";
import {
  AuditDashboardScratcherTab,
  buildManualSoldOutEligibilityRequest,
  calculateDefaultTotals,
  countGames,
  countMismatchedGames,
  countPacks,
} from "../../utils";
import AuditDetailScratcherTable from "./AuditDetailScratcherTable";
import type { DailyCashierShiftNote } from "../../utils/cashierShiftNotes";

interface AuditDetailScratcherSectionProps {
  reportDate?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  embedded?: boolean;
  defaultTab?: AuditDashboardScratcherTab;
  cashierShiftNotes?: DailyCashierShiftNote[];
}

export default function AuditDetailScratcherSection({
  reportDate,
  startDate,
  endDate,
  embedded = false,
  defaultTab = "all",
  cashierShiftNotes = [],
}: AuditDetailScratcherSectionProps) {
  const { id: storeId } = useParams<{ id: string }>();
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<AuditDashboardScratcherTab>(defaultTab);
  const [markingSoldOutKey, setMarkingSoldOutKey] = useState<string | null>(null);
  const resolvedStartDate = startDate ?? reportDate ?? null;
  const resolvedEndDate = endDate ?? startDate ?? reportDate ?? null;
  const dailyExpandedQuery = useAuditDashboardDailyExpandedReport(storeId, resolvedStartDate, resolvedEndDate);
  const { mutateAsync: markMovedPackSoldOut } = useMarkMovedPackSoldOutMutation(
    storeId,
    resolvedStartDate,
    resolvedEndDate
  );
  const { data: allStores = [], isLoading: isStoresLoading } = useStoresSummaryStoresOnly(undefined, {
    enabled: Boolean(storeId),
  });

  const storeDetails = useMemo(() => {
    if (!storeId) return undefined;
    return allStores.find((item) => item.store.id === storeId)?.store;
  }, [allStores, storeId]);

  const isBothConnected = Boolean(
    storeDetails?.pos_type && storeDetails.pos_type !== "n/a" && storeDetails?.lotto_authorized
  );
  const isPOSConnectedOnly = Boolean(
    storeDetails?.pos_type && storeDetails.pos_type !== "n/a" && !storeDetails?.lotto_authorized
  );
  const hasPosType = isBothConnected || isPOSConnectedOnly;

  const dailyExpandedItems = dailyExpandedQuery.data?.items;
  const dailyExpandedMovedItems = dailyExpandedQuery.data?.movedToBackstockItems;
  const allRows = useMemo(() => dailyExpandedItems ?? [], [dailyExpandedItems]);
  const movedRows = useMemo(() => dailyExpandedMovedItems ?? [], [dailyExpandedMovedItems]);
  const isMovedToBackstockView = activeTab === "moved_to_backstock";
  const activeDefaultRows = useMemo(
    () => (activeTab === "mismatched" ? allRows.filter((item) => item.hasVariance) : allRows),
    [activeTab, allRows]
  );
  const movedPackEligibilityRequest = useMemo(
    () => (isMovedToBackstockView ? buildManualSoldOutEligibilityRequest(movedRows) : []),
    [isMovedToBackstockView, movedRows]
  );
  const manualSoldOutEligibilityQuery = useAuditDashboardManualSoldOutEligibility(
    storeId,
    movedPackEligibilityRequest,
    isMovedToBackstockView
  );
  const mismatchedCount = countMismatchedGames(allRows);
  const movedCount = movedRows.length;
  const totalPacksCount = isMovedToBackstockView ? movedCount : countPacks(activeDefaultRows);
  const scratcherTotals = useMemo(() => calculateDefaultTotals(allRows), [allRows]);
  const summaryPacksCount = countPacks(allRows);
  const summaryGamesCount = countGames(allRows);
  const summaryTicketsCount = scratcherTotals.qtySold || scratcherTotals.scratcherCount;
  const isLoading = dailyExpandedQuery.isLoading || isStoresLoading;

  useEffect(() => {
    if (!hasPosType && activeTab === "mismatched") {
      setActiveTab("all");
    }
  }, [activeTab, hasPosType]);

  useEffect(() => {
    setActiveTab(hasPosType ? defaultTab : "all");
  }, [defaultTab, hasPosType, resolvedEndDate, resolvedStartDate]);

  const handleTabChange = (_event: React.SyntheticEvent, nextTab: AuditDashboardScratcherTab) => {
    setActiveTab(nextTab);
  };

  const handleAccordionChange = (_event: React.SyntheticEvent, nextExpanded: boolean) => {
    setExpanded(nextExpanded);

    if (nextExpanded) {
      setActiveTab(hasPosType ? defaultTab : "all");
    }
  };

  const handleMarkMovedPackSoldOut = useCallback(
    async (item: MovedToBackstockItem) => {
      if (!storeId || !item.gameId || !item.packId) return;

      const rowKey = `${item.gameId}-${item.packId}`;
      setMarkingSoldOutKey(rowKey);
      try {
        await markMovedPackSoldOut(item);
      } catch {
        // The mutation hook owns user-facing error messaging.
      } finally {
        setMarkingSoldOutKey(null);
      }
    },
    [markMovedPackSoldOut, storeId]
  );

  const scratcherDetailContent = (
    <>
      {dailyExpandedQuery.isError ? (
        <Box sx={{ px: 1.5, py: 3 }}>
          <Typography component="div" sx={{ fontSize: 12, color: "text.secondary", opacity: 0.72 }}>
            Unable to load scratcher details.
          </Typography>
        </Box>
      ) : (
        <>
          <AuditDetailScratcherTable
            activeTab={activeTab}
            scratcherCount={summaryGamesCount}
            mismatchedCount={mismatchedCount}
            movedCount={movedCount}
            defaultRows={activeDefaultRows}
            movedRows={movedRows}
            hasPosType={hasPosType}
            isLoading={isLoading}
            totalPacksCount={totalPacksCount}
            cashierShiftNotes={activeTab === "mismatched" ? cashierShiftNotes : []}
            onTabChange={handleTabChange}
            manualSoldOutEligibility={manualSoldOutEligibilityQuery.data ?? []}
            isManualSoldOutEligibilityLoading={manualSoldOutEligibilityQuery.isFetching}
            markingSoldOutKey={markingSoldOutKey}
            storeId={storeId}
            onMarkMovedPackSoldOut={handleMarkMovedPackSoldOut}
          />
        </>
      )}
    </>
  );

  if (embedded) {
    return <Box sx={{ px: "8px", pb: 1.5 }}>{scratcherDetailContent}</Box>;
  }

  return (
    <Box
      sx={{
        borderTop: "1px solid",
        borderColor: auditDashboardTheme.colors.containerBorder,
        px: 1.5,
        py: 1.5,
      }}
    >
      <Typography component="div" sx={{ fontSize: 16, fontWeight: 600, color: "#222b45", lineHeight: 1.2 }}>
        {`Scratcher (${summaryGamesCount.toLocaleString()})`}
      </Typography>

      <Accordion
        expanded={expanded}
        onChange={handleAccordionChange}
        disableGutters
        elevation={0}
        sx={{
          mt: 1.2,
          border: "1px solid",
          borderColor: auditDashboardTheme.colors.containerBorder,
          borderRadius: "10px !important",
          overflow: "hidden",
          "&:before": { display: "none" },
          boxShadow: "none",
          "&.Mui-expanded": {
            margin: 0,
            borderRadius: "10px !important",
          },
          "& .MuiAccordionSummary-root": {
            minHeight: 56,
            px: "14px",
            py: "12px",
          },
          "& .MuiAccordionSummary-root.Mui-expanded": {
            minHeight: 56,
          },
          "& .MuiAccordionSummary-content": {
            margin: 0,
          },
          "& .MuiAccordionSummary-content.Mui-expanded": {
            margin: 0,
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ChevronRightIcon sx={{ fontSize: 20, color: auditDashboardTheme.colors.blue }} />}
          sx={{
            "& .MuiAccordionSummary-expandIconWrapper": {
              color: auditDashboardTheme.colors.blue,
              transform: "none",
            },
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
              transform: "rotate(90deg)",
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 2,
              minWidth: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, color: "#222b45", flexShrink: 0 }}>
                Scratcher
              </Typography>
            </Box>
            <Typography
              component="strong"
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: "#222b45",
                whiteSpace: "nowrap",
              }}
            >
              {`${summaryPacksCount.toLocaleString()} ${summaryPacksCount === 1 ? "pack" : "packs"} | ${summaryTicketsCount.toLocaleString()} tickets`}
            </Typography>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0, borderTop: 0 }}>
          {scratcherDetailContent}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
